import { IcoImage } from '../../assets/icons';
import { useEffect, useRef } from 'react';

/* ─────────────────────────────────────────────────────────
   Img — render real image or a clean placeholder
   ───────────────────────────────────────────────────────── */
export default function Img({ src, alt = '', label, className = '', style }) {
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className={`object-cover ${className}`}
        style={style}
        loading="lazy"
      />
    );
  }

  return (
    <div
      className={`img-ph ${className}`}
      style={style}
      role="img"
      aria-label={alt || label}
    >
      <IcoImage cls="w-8 h-8 text-brand-300 dark:text-brand-800" />
      {label && (
        <span className="text-[11px] text-brand-400 dark:text-brand-700 font-medium text-center px-6 leading-relaxed">
          {label}
        </span>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Convert Google Drive share URL → embed URL
   ───────────────────────────────────────────────────────── */
function toGDriveEmbed(url) {
  if (!url) return null;
  // Already an embed URL
  if (url.includes('/preview') || url.includes('embed')) return url;
  // Share URL: https://drive.google.com/file/d/FILE_ID/view
  const m = url.match(/\/file\/d\/([^/]+)/);
  if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
  return url;
}

/* ─────────────────────────────────────────────────────────
   GDriveVideo — auto-play when in view, pause when out
   Muted, no controls on background style, with controls on normal
   ───────────────────────────────────────────────────────── */
export function GDriveVideo({
  src,
  title = '',
  className = '',
  style,
  background = false,
  showControls = true,
}) {
  const embedUrl = toGDriveEmbed(src);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        // Post message to control play/pause via postMessage (works for GDrive)
        try {
          if (entry.isIntersecting) {
            iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          } else {
            iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          }
        } catch (_) {}
        // Reload iframe src to trigger autoplay on intersection
        if (entry.isIntersecting && iframe.dataset.paused === 'true') {
          iframe.dataset.paused = 'false';
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!embedUrl) return null;

  const finalUrl = embedUrl + (embedUrl.includes('?') ? '&' : '?') +
    'autoplay=1&mute=1&loop=1&controls=' + (showControls ? '1' : '0');

  return (
    <div ref={containerRef} className={`overflow-hidden ${className}`} style={style}>
      <iframe
        ref={iframeRef}
        src={finalUrl}
        title={title}
        className="w-full h-full border-0"
        allow="autoplay; fullscreen"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   VideoPlaceholder — kept for backward compat
   Now uses GDriveVideo if src is a Google Drive URL
   ───────────────────────────────────────────────────────── */
export function VideoPlaceholder({ src, title = '', className = '' }) {
  if (src) {
    return (
      <GDriveVideo
        src={src}
        title={title}
        className={`rounded-2xl aspect-video ${className}`}
        showControls={true}
      />
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-brand-950 flex flex-col items-center justify-center gap-4 cursor-pointer group ${className}`}
    >
      <div className="absolute inset-0 opacity-[0.04]">
        <svg width="100%" height="100%">
          <defs>
            <pattern id="vgrid" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#ea580c" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#vgrid)"/>
        </svg>
      </div>
      <div className="absolute inset-0 bg-gradient-to-br from-brand-800/30 via-transparent to-brand-900/20" />
      <div className="relative z-10 w-20 h-20 rounded-full bg-brand-600 flex items-center justify-center group-hover:bg-brand-500 group-hover:scale-105 transition-all duration-300 shadow-2xl shadow-brand-600/40">
        <svg viewBox="0 0 24 24" className="w-8 h-8 fill-white ml-1">
          <polygon points="5 3 19 12 5 21 5 3"/>
        </svg>
      </div>
      {title && (
        <p className="relative z-10 text-sm text-brand-300 font-medium text-center px-8">
          {title}
        </p>
      )}
    </div>
  );
}
